import type { ContentRegistry } from '../registry/registry';
import type { ExternalEvidence } from '../types';
import { EXTERNAL_EVIDENCE_ORIGINS } from '../types';
import { FORBIDDEN_CLAIMS } from './validate';

export const EXTERNAL_TITLE_MAX = 120;
export const EXTERNAL_SUMMARY_MAX = 1000;

/**
 * Claims external evidence may not make. The learner may name an employer, a school or a project in
 * their own words (the item is labeled self-reported), but nothing may claim that WorkSim or anyone
 * else verified, certified or endorsed it, or that WorkSim work was employment.
 */
const EXTERNAL_CLAIMS: readonly { re: RegExp; message: string }[] = [
  { re: /\bverified\b/i, message: 'Do not describe the work as verified: WorkSim labels external evidence self-reported and does not verify it.' },
  { re: /\bcertified by\b/i, message: 'Do not write "certified by": external evidence cannot claim a certification.' },
  { re: /\bendorsed by\b/i, message: 'Do not write "endorsed by": external evidence cannot claim an endorsement.' },
  { re: /\baccredited by\b/i, message: 'Do not write "accredited by": external evidence cannot claim an accreditation.' },
  { re: /\bworksim[- ](certified|approved|validated)\b/i, message: 'WorkSim does not certify, approve or validate external evidence.' },
  { re: /\b(employed|hired|worked|job|position)\b[^.]{0,40}\btechcore\b/i, message: 'TechCore Solutions is WorkSim\'s fictional company; work there is simulation, never employment.' },
];

/**
 * Check one piece of learner-supplied evidence before the server stores it. Returns plain-words
 * problems; an empty list means it may be stored. External evidence never carries a score, and
 * `simulation` and `lab` are reserved for work WorkSim produced. Pass the registry to also check
 * that tagged skills and the career exist.
 */
export function validateExternalEvidence(item: ExternalEvidence, registry?: ContentRegistry): string[] {
  const problems: string[] = [];
  const title = typeof item.title === 'string' ? item.title.trim() : '';
  const summary = typeof item.summary === 'string' ? item.summary.trim() : '';
  if (!title) problems.push('A title is required.');
  else if (title.length > EXTERNAL_TITLE_MAX) problems.push(`The title is ${title.length} characters; keep it to ${EXTERNAL_TITLE_MAX}.`);
  if (!summary) problems.push('A summary is required.');
  else if (summary.length > EXTERNAL_SUMMARY_MAX) problems.push(`The summary is ${summary.length} characters; keep it to ${EXTERNAL_SUMMARY_MAX.toLocaleString('en-US')}.`);
  if (!(EXTERNAL_EVIDENCE_ORIGINS as readonly string[]).includes(item.origin)) {
    problems.push(`Origin must be one of ${EXTERNAL_EVIDENCE_ORIGINS.join(', ')}; simulation and lab evidence comes only from work completed in WorkSim.`);
  }
  if (typeof item.completedAt !== 'string' || Number.isNaN(Date.parse(item.completedAt))) problems.push('completedAt must be a date.');
  if (item.link !== undefined && !/^https:\/\/\S+$/i.test(item.link)) problems.push('A link must be an absolute https URL.');
  if ('score' in (item as object) || (item.skillIds as unknown[] | undefined)?.some((s) => typeof s !== 'string')) problems.push('External evidence carries no score.');
  const text = `${title} ${summary}`;
  for (const c of EXTERNAL_CLAIMS) if (c.re.test(text)) problems.push(c.message);
  const lower = text.toLowerCase();
  for (const p of FORBIDDEN_CLAIMS) if (lower.includes(p)) problems.push(`Remove the claim "${p}".`);
  if (registry) {
    if (!registry.findCareer(item.careerId)) problems.push(`Unknown career ${String(item.careerId)}.`);
    for (const id of item.skillIds ?? []) if (!registry.findSkill(id)) problems.push(`Unknown skill ${String(id)}.`);
  }
  return problems;
}
